import React, { useEffect, useState } from 'react';
import Icon from 'src/components/common/Icon';
import { formatTransactionCode, parseTransactionId } from './Transactions.helpers';
import { SearchError, SearchPill } from './Transactions.styles';

const PLACEHOLDER = 'TX-48135';
const LABEL = 'Buscar por número de transacción';
const INVALID_COPY = 'Escribe el número, por ejemplo TX-48135';

interface TransactionSearchProps {
    /** Número que el listado está mostrando; `null` cuando no hay búsqueda. */
    value: number | null;
    onSearch: (transactionId: number) => void;
    onClear: () => void;
}

/**
 * Búsqueda por número de transacción, para atender un reclamo que llega con el
 * número en la mano sin paginar el listado.
 *
 * Vive dentro de la pestaña y no en el buscador central de /retention: ese
 * buscador es de todas las pestañas y su contrato es "un cliente por correo"
 * —valida formato de correo y comprueba que el cliente exista antes de aceptar—,
 * así que un segundo tipo de búsqueda obligaría a las otras pestañas a entender
 * un input que no saben usar.
 *
 * Vaciar el campo y buscar es la forma de volver al listado, así que un campo
 * vacío no es un error.
 */
const TransactionSearch = ({ value, onSearch, onClear }: TransactionSearchProps) => {
    const [text, setText] = useState('');
    const [invalid, setInvalid] = useState(false);

    // El número vigente lo manda el estado del listado, no este campo: así el
    // botón "Ver el listado" del aviso también limpia el input.
    useEffect(() => {
        setText(value === null ? '' : formatTransactionCode(value));
        setInvalid(false);
    }, [value]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        if (text.trim() === '') {
            setInvalid(false);
            onClear();
            return;
        }

        const transactionId = parseTransactionId(text);
        if (transactionId === null) {
            setInvalid(true);
            return;
        }

        setInvalid(false);
        onSearch(transactionId);
    };

    const handleClear = () => {
        setText('');
        setInvalid(false);
        if (value !== null) onClear();
    };

    return (
        <>
            <SearchPill $invalid={invalid} onSubmit={handleSubmit} role="search">
                <span className="search-icon">
                    <Icon icon="pcom-icon-search" size={13} />
                </span>
                <input
                    type="text"
                    inputMode="numeric"
                    data-id="transactions-search-input"
                    aria-label={LABEL}
                    placeholder={PLACEHOLDER}
                    value={text}
                    onChange={(event) => {
                        setText(event.target.value);
                        if (invalid) setInvalid(false);
                    }}
                />
                {text !== '' && (
                    <button
                        type="button"
                        className="search-action"
                        data-id="transactions-search-clear"
                        aria-label="Limpiar la búsqueda por número"
                        onClick={handleClear}
                    >
                        <Icon icon="pcom-icon-close" size={11} />
                    </button>
                )}
                <button
                    type="submit"
                    className="search-action"
                    data-id="transactions-search-submit"
                    aria-label={LABEL}
                >
                    <Icon icon="pcom-icon-arrow-right" size={12} />
                </button>
            </SearchPill>
            {invalid && (
                <SearchError role="alert" data-id="transactions-search-error">
                    {INVALID_COPY}
                </SearchError>
            )}
        </>
    );
};

export default TransactionSearch;
